/** Wall clock ⇄ instant in any IANA zone, on `Intl` alone (no dependency).
 *
 * Generalises `components/TimeWindowPicker/timezone.ts` from utc/local to IANA zones and
 * keeps its idiom: date-fns has no timezone support and MUI's `timezone` prop does not
 * work with the date-fns adapter, so pickers are fed plain `Date`s whose LOCAL fields carry
 * the wall clock in the target zone (the tour's). Minute-granular — seconds are dropped,
 * as on the wire (`YYYY-MM-DD`, `HH:MM`, `YYYY-MM-DDTHH:MM`, tour-local).
 *
 * Wall clock → instant across DST:
 * - **Spring-forward gap** (a wall time that does not exist, e.g. 01:30 Europe/London on
 *   29 Mar 2026): the next valid minute — the transition itself (02:00 BST).
 * - **Fall-back overlap** (a wall time that happens twice, e.g. 01:30 Europe/London on
 *   25 Oct 2026): the earlier instant, on the pre-transition offset (01:30 BST), unless
 *   `{ overlap: 'later' }` (01:30 GMT).
 *
 * Known edge, inherited from the picker idiom (~1 h a year): a wall clock inside the
 * BROWSER zone's own spring-forward gap cannot be held by a Date's local fields — the JS
 * engine nudges it. The `WallClock` / string forms are exact; only the picker-`Date` forms
 * (`instantToZonedWallClock`, a Date passed to `zonedWallClockToInstant`) have the edge.
 *
 * An unknown zone makes the conversions throw a RangeError (check with `isValidTimeZone`);
 * `formatZoned` falls back to UTC, labelled "UTC", so a bad zone never renders a wrong one.
 */

/** A wall-clock reading, minute precision. `month` is 1–12. */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    partsFormatters.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    partsFormatter(tz);
    return true;
  } catch {
    return false;
  }
}

function zonedFields(t: number, tz: string): WallClock & { second: number } {
  const out = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const p of partsFormatter(tz).formatToParts(t)) {
    if (p.type in out) out[p.type as keyof typeof out] = Number(p.value);
  }
  // Some engines render midnight as 24 even with h23.
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** The zone's offset from UTC at `instant`, in minutes (London in summer 60, Kathmandu 345,
 *  New York in winter −300). */
export function zoneOffsetMinutes(instant: Date | number, tz: string): number {
  const t = typeof instant === 'number' ? instant : instant.getTime();
  const s = Math.floor(t / 1000) * 1000;
  const f = zonedFields(s, tz);
  const asUtc = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
  return Math.round((asUtc - s) / MINUTE_MS);
}

/** Instant → its wall clock in `tz`. */
export function zonedWallClock(instant: Date, tz: string): WallClock {
  const { year, month, day, hour, minute } = zonedFields(instant.getTime(), tz);
  return { year, month, day, hour, minute };
}

/** A picker `Date` (local fields = wall clock) → its wall clock. */
export function pickerDateToWallClock(d: Date): WallClock {
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

/** A wall clock → a picker `Date` whose local fields show it. */
export function wallClockToPickerDate(w: WallClock): Date {
  return new Date(w.year, w.month - 1, w.day, w.hour, w.minute);
}

/** Instant → a Date whose LOCAL fields show the wall clock in `tz` (for MUI pickers). */
export function instantToZonedWallClock(instant: Date, tz: string): Date {
  return wallClockToPickerDate(zonedWallClock(instant, tz));
}

/** Inverse: a wall clock in `tz` (a picker Date's local fields, or a WallClock) → instant.
 *  Gap → the next valid minute; overlap → the earlier instant unless `overlap: 'later'`. */
export function zonedWallClockToInstant(
  wall: Date | WallClock,
  tz: string,
  { overlap = 'earlier' }: { overlap?: 'earlier' | 'later' } = {},
): Date {
  const w = wall instanceof Date ? pickerDateToWallClock(wall) : wall;
  const t0 = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);
  // The offsets on either side of any transition near this wall time (zones change at
  // most once within a day; the instant is within ±14 h of t0).
  const before = zoneOffsetMinutes(t0 - DAY_MS, tz);
  const after = zoneOffsetMinutes(t0 + DAY_MS, tz);
  const candidates = [...new Set([before, after])]
    .map((o) => t0 - o * MINUTE_MS)
    .filter((t) => t0 - zoneOffsetMinutes(t, tz) * MINUTE_MS === t)
    .sort((a, b) => a - b);
  if (candidates.length > 0) {
    return new Date(overlap === 'later' ? candidates[candidates.length - 1]! : candidates[0]!);
  }
  // Gap: no instant shows this wall time. The transition — the first minute on the new
  // offset — lies between the two failed candidates; find it by bisection.
  let lo = Math.min(t0 - before * MINUTE_MS, t0 - after * MINUTE_MS);
  let hi = Math.max(t0 - before * MINUTE_MS, t0 - after * MINUTE_MS);
  const loOffset = zoneOffsetMinutes(lo, tz);
  while (hi - lo > MINUTE_MS) {
    const mid = lo + Math.floor((hi - lo) / (2 * MINUTE_MS)) * MINUTE_MS;
    if (zoneOffsetMinutes(mid, tz) === loOffset) lo = mid;
    else hi = mid;
  }
  return new Date(hi);
}

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/;

/** `YYYY-MM-DD` (midnight) or `YYYY-MM-DDTHH:MM` (also with a space) → WallClock; null for
 *  anything else, including field rollover (Feb 30, month 13, hour 24). */
export function parseWallClock(value: string | null | undefined): WallClock | null {
  const m = value ? WALL_RE.exec(value) : null;
  if (!m) return null;
  const w: WallClock = {
    year: +m[1]!,
    month: +m[2]!,
    day: +m[3]!,
    hour: m[4] === undefined ? 0 : +m[4],
    minute: m[5] === undefined ? 0 : +m[5],
  };
  const check = new Date(Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute));
  const ok =
    check.getUTCFullYear() === w.year &&
    check.getUTCMonth() === w.month - 1 &&
    check.getUTCDate() === w.day &&
    check.getUTCHours() === w.hour &&
    check.getUTCMinutes() === w.minute;
  return ok ? w : null;
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/** WallClock → `YYYY-MM-DDTHH:MM` (or `YYYY-MM-DD` with `{ time: false }`). */
export function formatWallClock(w: WallClock, { time = true }: { time?: boolean } = {}): string {
  const date = `${pad(w.year, 4)}-${pad(w.month)}-${pad(w.day)}`;
  return time ? `${date}T${pad(w.hour)}:${pad(w.minute)}` : date;
}

/** Instant → the wire datetime `YYYY-MM-DDTHH:MM` in `tz`. */
export function instantToZonedString(instant: Date, tz: string): string {
  return formatWallClock(zonedWallClock(instant, tz));
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

export interface FormatZonedOptions {
  /** "Fri" (default true). */
  weekday?: boolean;
  /** "09:30" (default true). */
  time?: boolean;
  /** `auto` (default) shows the year only when it is not the current year in `tz`. */
  year?: 'auto' | 'always' | 'never';
  /** Append the IANA zone name (default true) — booking times are never unlabelled. */
  zone?: boolean;
  /** Reference instant for `year: 'auto'` (default now). */
  now?: Date;
}

const labelFormatters = new Map<string, Intl.DateTimeFormat>();

function labelFormatter(tz: string): Intl.DateTimeFormat {
  let f = labelFormatters.get(tz);
  if (!f) {
    // en-US names ("Sep", not en-GB's "Sept"), assembled day-first below.
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    labelFormatters.set(tz, f);
  }
  return f;
}

function toInstant(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** An instant's label parts in `zoneName` (a valid zone): "Fri", "2", "Oct", "2026",
 *  "09:30". */
function labelParts(d: Date, zoneName: string) {
  const parts: Record<string, string> = {};
  for (const p of labelFormatter(zoneName).formatToParts(d)) parts[p.type] = p.value;
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return {
    weekday: parts.weekday ?? '',
    day: parts.day ?? '',
    month: parts.month ?? '',
    year: parts.year ?? '',
    time: `${hour}:${parts.minute}`,
  };
}

/** "Fri 2 Oct 09:30 Europe/London" — an instant in the tour's zone, labelled. "—" for an
 *  empty or invalid instant. */
export function formatZoned(
  instant: Date | string | number | null | undefined,
  tz: string | null | undefined,
  { weekday = true, time = true, year = 'auto', zone = true, now }: FormatZonedOptions = {},
): string {
  const d = toInstant(instant);
  if (!d) return '—';
  const zoneName = isValidTimeZone(tz) ? tz : 'UTC';
  const parts = labelParts(d, zoneName);
  const showYear =
    year === 'always' ||
    (year === 'auto' &&
      zonedWallClock(d, zoneName).year !== zonedWallClock(now ?? new Date(), zoneName).year);
  const out = [
    weekday ? parts.weekday : null,
    parts.day,
    parts.month,
    showYear ? parts.year : null,
    time ? parts.time : null,
    zone ? zoneName : null,
  ];
  return out.filter(Boolean).join(' ');
}

export interface FormatZonedRangeOptions extends FormatZonedOptions {
  /** Write only what the two ends do not share (default true) — one day: "Sat 21 Nov ·
   *  07:45–13:45"; a date range (`time: false`): "12–14 Oct", "30 Sep–6 Oct" (no weekday).
   *  False writes both ends in full, each as `formatZoned` would: "Sat 3 Oct 07:30 – Sat
   *  3 Oct 12:00". */
  collapse?: boolean;
  /** In place of a missing start (default "no start"). */
  openStart?: string;
  /** In place of a missing end (default "no end"). */
  openEnd?: string;
}

/** "Sat 21 Nov · 07:45–13:45 Europe/Rome", "12–14 Oct Europe/Rome", "Wed 30 Sep 07:30 –
 *  Tue 6 Oct 12:00 Europe/Rome" — a range of instants in one zone, the zone named once at
 *  the end. Days are compared in the zone, never the browser's. The year shows when either
 *  end is not in the current year (`year: 'auto'`); a range that crosses a year shows both.
 *  A missing (or invalid) end reads `openStart` / `openEnd`, the other end in full; "—"
 *  when both are missing. An unknown zone falls back to UTC, labelled "UTC". */
export function formatZonedRange(
  start: Date | string | number | null | undefined,
  end: Date | string | number | null | undefined,
  tz: string | null | undefined,
  {
    collapse = true,
    openStart = 'no start',
    openEnd = 'no end',
    weekday = true,
    time = true,
    year = 'auto',
    zone = true,
    now,
  }: FormatZonedRangeOptions = {},
): string {
  const s = toInstant(start);
  const e = toInstant(end);
  if (!s && !e) return '—';
  const zoneName = isValidTimeZone(tz) ? tz : 'UTC';
  const label = zone ? ` ${zoneName}` : '';
  const full = (d: Date, y: FormatZonedOptions['year']) =>
    formatZoned(d, zoneName, { weekday, time, year: y, zone: false, now });

  if (!s || !e || !collapse) {
    return `${s ? full(s, year) : openStart} – ${e ? full(e, year) : openEnd}${label}`;
  }

  const a = labelParts(s, zoneName);
  const b = labelParts(e, zoneName);
  const thisYear = labelParts(now ?? new Date(), zoneName).year;
  const showYear =
    year === 'always' || (year === 'auto' && (a.year !== thisYear || b.year !== thisYear));
  const sameYear = a.year === b.year;
  const sameMonth = sameYear && a.month === b.month;
  const sameDay = sameMonth && a.day === b.day;

  if (sameDay) {
    const day = [weekday ? a.weekday : null, a.day, a.month, showYear ? a.year : null]
      .filter(Boolean)
      .join(' ');
    if (!time) return `${day}${label}`;
    return `${day} · ${a.time === b.time ? a.time : `${a.time}–${b.time}`}${label}`;
  }
  if (time) {
    const y = showYear ? 'always' : 'never';
    return `${full(s, y)} – ${full(e, y)}${label}`;
  }
  const yearOf = (p: { year: string }) => (showYear ? ` ${p.year}` : '');
  if (sameMonth) return `${a.day}–${b.day} ${a.month}${yearOf(a)}${label}`;
  if (sameYear) return `${a.day} ${a.month}–${b.day} ${b.month}${yearOf(a)}${label}`;
  return `${a.day} ${a.month}${yearOf(a)}–${b.day} ${b.month}${yearOf(b)}${label}`;
}

const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>();

function relativeFormatter(style: 'long' | 'narrow'): Intl.RelativeTimeFormat {
  let f = relativeFormatters.get(style);
  if (!f) {
    f = new Intl.RelativeTimeFormat('en', { numeric: 'always', style });
    relativeFormatters.set(style, f);
  }
  return f;
}

/** "in 2 days", "3 hours ago", "now" (`narrow`: "in 2d", "3h ago") — elapsed time, so it
 *  does not depend on any zone. Thresholds: under 45 s "now", then minutes under 45 min,
 *  hours under 22 h, days under 26 d, months under 11 months, then years. "—" for an
 *  empty or invalid instant. */
export function formatRelative(
  instant: Date | string | number | null | undefined,
  { now = new Date(), style = 'long' }: { now?: Date; style?: 'long' | 'narrow' } = {},
): string {
  const d = toInstant(instant);
  if (!d) return '—';
  const diff = d.getTime() - now.getTime();
  const abs = Math.abs(diff);
  if (abs < 45_000) return 'now';
  const fmt = relativeFormatter(style);
  if (abs < 45 * MINUTE_MS) return fmt.format(Math.round(diff / MINUTE_MS), 'minute');
  if (abs < 22 * 60 * MINUTE_MS) return fmt.format(Math.round(diff / (60 * MINUTE_MS)), 'hour');
  if (abs < 26 * DAY_MS) return fmt.format(Math.round(diff / DAY_MS), 'day');
  if (abs < 11 * 30 * DAY_MS) return fmt.format(Math.round(diff / (30 * DAY_MS)), 'month');
  return fmt.format(Math.round(diff / (365 * DAY_MS)), 'year');
}
